// src/components/CheckOut.tsx
import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { createOrder, updateOrder, updateTableStatus } from '../utils/api'; // Import API functions

const CheckOut: React.FC = () => {
    const { settings } = useSettings();
    const { cartItems } = useCart();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTable, setSelectedTable] = useState<any>(() => {
        const orderType = localStorage.getItem('orderType') || 'You need to select an order type';
        return orderType === 'Takeaway'
            ? 0
            : JSON.parse(localStorage.getItem('selectedTable') || '{}') || { id: 'You need to select a table' };
    });
    const navigate = useNavigate();

    const vendorId = JSON.parse(localStorage.getItem('user') || '{}').vendor_id;
    const token = localStorage.getItem('token');
    const customerId = 0; // Set actual customer ID if available
    const orderType = localStorage.getItem('orderType') || 'You need to select an order type';
    const orderId: any = localStorage.getItem('orderId');

    useEffect(() => {
        if (orderType === 'Takeaway') {
            setSelectedTable(0);
        }
    }, [orderType]);

    const handleCheckout = async () => {
        if (!cartItems || cartItems.length === 0) {
            setError("Your cart is empty. Please add items before checking out.");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            if (!vendorId || !token) {
                throw new Error("Missing vendor ID or authorization token");
            }

            // const items = cartItems.map(item => ({
            //     productId: item.productId,
            //     name: item.name,
            //     price: item.price,
            //     quantity: item.quantity,
            //     addons: item.options.add_ons?.map(addon => addon.name).join(", ") || "",
            //     addonDetails: item.options.add_ons?.map(addon => ({ name: addon.name, price: addon.price })) || [],
            //     toppings: item.options.toppings?.map(topping => topping.name).join(", ") || "",
            //     toppingDetails: item.options.toppings?.map(topping => ({ name: topping.name, price: topping.price })) || [],
            //     specifications: item.options.specifications?.map(spec => ({
            //         specification_id: spec.keyid,
            //         option_id: spec.keyid,
            //         option_name: spec.name,
            //         extra_cost: spec.price,
            //     })) || [],
            //     totalPrice: item.totalPrice,
            // }));

            const items = cartItems.map(item => ({
                productId: item.productId,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                addons: item.options.add_ons?.map(addon => addon.name).join(", ") || "",
                addonDetails: Array.isArray(item.options.add_ons)
                    ? item.options.add_ons.map(addon => ({
                        name: addon.name,
                        price: addon.price,
                    }))
                    : [], // Ensure add_ons is an array
                toppings: Array.isArray(item.options.toppings)
                    ? item.options.toppings.map(topping => topping.name).join(", ")
                    : "", // Ensure toppings is an array and join them
                toppingDetails: Array.isArray(item.options.toppings)
                    ? item.options.toppings.map(topping => ({
                        name: topping.name,
                        price: topping.price,
                    }))
                    : [], // Ensure toppings is an array
                specifications: Array.isArray(item.options.specifications)
                    ? item.options.specifications.map(spec => ({
                        specification_id: spec.keyid,
                        option_id: spec.keyid,
                        option_name: spec.name,
                        extra_cost: spec.price,
                    }))
                    : [], // Ensure specifications is an array
                totalPrice: item.totalPrice,
            }));

            // const payload = {
            //     customerId,
            //     orderType,
            //     tableId: selectedTable.id || selectedTable,
            //     items,
            //     status: 'occupied',
            //     amountToPay: cartItems.reduce((acc, item) => acc + item.totalPrice, 0),
            //     userId: vendorId,
            //     table: selectedTable,
            // };
            const payload = {
                customerId: customerId,
                orderType: orderType,
                tableId: selectedTable.id || selectedTable,
                items: items,
                status: 'pending',
                amountToPay: cartItems.reduce((acc, item) => acc + item.totalPrice, 0),
                userId: vendorId,
                table: selectedTable,
            };

            let response;

            if (orderId) {
                response = await updateOrder(vendorId, orderId, payload, token);
            } else {
                response = await createOrder(vendorId, payload, token);
            }

            if (response.status === 200) {
                const newOrderId = response.data.id || orderId;

                // console.log("Order created/updated successfully with ID:", newOrderId);
                // console.log("b4 updateTableStatus:", selectedTable);
                if (orderType === 'Dine In' && selectedTable?.id) {
                    try {
                        await updateTableStatus(vendorId, selectedTable.id, 'occupied', newOrderId, customerId);
                    } catch (err) {
                        console.error("Error in updateTableStatus:", err);
                    }
                }
                // console.log("after updateTableStatus:", selectedTable);

                const checkedOutItems = cartItems.map(item => ({ ...item, checkedOut: true }));
                localStorage.setItem('checkedOutItems', JSON.stringify(checkedOutItems));
                localStorage.setItem('existingOrderId', newOrderId);

                navigate(`/order-details/${newOrderId}`);
            } else {
                throw new Error("Order creation/update failed with status: " + response.status);
            }
        } catch (err: any) {
            console.error("Checkout error:", err);
            setError(err.response?.data?.message + " - An error occurred while processing your order.");
        } finally {
            setIsLoading(false);
        }
    };

    const canCheckout =
        cartItems.length > 0 &&
        orderType !== 'You need to select an order type' &&
        (orderType === 'Dine In' ? selectedTable && !isNaN(Number(selectedTable.id)) : true);

    return (
        <div className="app-page">
            <div className="w-auto mt-10 flex flex-col justify-center place-items-start p-4 border border-1 border-red-500 rounded-xl shadow">
                <h2 className="pageTitle">
                    Checkout&nbsp;<span>for #{localStorage.getItem('orderId') || 'New Order'}</span>
                </h2>
                <p>Order Type: {orderType}</p>
                {/* {selectedTable !== 0 && <p>Table ID: {selectedTable?.id || selectedTable}</p>} */}
                {selectedTable !== 0 && <p>Table Name: {selectedTable?.tableName || selectedTable}</p>}
                <p>Item Quantity: {cartItems.reduce((acc, item) => acc + item.quantity, 0)}</p>
                <p className="text-lg font-bold mb-4 ">
                    Total Amount to Pay: {settings?.currencyName}
                    {cartItems.reduce((acc, item) => acc + item.totalPrice, 0).toFixed(2)}
                </p>
                {error && <p className="error-message">{error}</p>}
                <button
                    onClick={handleCheckout}
                    className={`${!canCheckout ? 'CancelDisabledBtn' : 'CancelBtn'}`}
                    disabled={!canCheckout}
                >
                    {isLoading ? 'Processing...' : 'Check Out Now'}
                </button>
            </div>
        </div>
    );
};

export default CheckOut;
